/**
 * GoodwinGuard Phase 1 — single source of truth for the operational sidebar.
 *
 * Replaces the five hand-maintained per-role nav trees that used to live inside
 * Layout.jsx. Every feature is declared ONCE here, assigned to one of the nine
 * canonical workflow sections. `buildNavSections(ctx)` returns the sections a
 * given role may see — access is identical to the legacy trees (nothing gained
 * or lost), just consolidated and de-duplicated.
 *
 * Canonical sections (hospice workflow order):
 *   Overview · Today · Admit · Document · Review · Bill · Program · Defend ·
 *   Executive · Trust & Admin
 */
import {
  LayoutDashboard, Gauge, ListChecks, Bell, Newspaper, UserPlus, CalendarClock,
  UserCheck, ClipboardCheck, ClipboardList, Sparkles, Mic, Users, FileSpreadsheet,
  FileCode, BookOpen, History, Smartphone, FileSearch, Shield, Upload, ShieldCheck,
  MessageSquare, Receipt, ShieldAlert, Activity, DollarSign, Database, BarChart3,
  BadgeCheck, Calendar, FileCheck, FileSignature, Scale, Zap, Building2, FileText,
  Target, Network, Globe, KeyRound, Plug, UsersRound, Building, Palette, CreditCard,
  GraduationCap, Settings, Crown, Package, Inbox, Waypoints, Route, Sun,
} from "lucide-react";

// Master definition — the superset of every nav item, grouped into 9 sections.
// Order here is the render order. An item shows only if its path is in the
// role's allowed set (see allowedPathsFor).
export const NAV_SECTIONS = [
  {
    key: "overview",
    title: "✨ Overview",
    items: [
      { path: "/app/feature-hub", label: "All Features", icon: Sparkles },
    ],
  },
  {
    key: "today",
    title: "📌 Today",
    items: [
      { path: "/app/my-day", label: "My Day", icon: Sun },
      { path: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { path: "/app/readiness", label: "Readiness Center", icon: Gauge },
      { path: "/app/compliance-queue", label: "Work Queue", icon: ListChecks },
      { path: "/app/compliance-alerts", label: "Compliance Alerts", icon: Bell },
      { path: "/app/daily-digest", label: "Daily Digest", icon: Newspaper },
    ],
  },
  {
    key: "admit",
    title: "🏥 Admit",
    items: [
      { path: "/app/patient-journey", label: "Patient Journey", icon: Route },
      { path: "/app/eligibility", label: "Eligibility Check", icon: UserPlus },
      { path: "/app/census-sync", label: "Census Sync", icon: CalendarClock },
      { path: "/app/care-teams", label: "Care Teams", icon: UsersRound },
      { path: "/app/patient-tracker", label: "Patient Tracker", icon: UserCheck },
      { path: "/app/hope-cockpit", label: "HOPE Cockpit", icon: CalendarClock },
      { path: "/app/hope-validation", label: "HOPE Validation", icon: ClipboardCheck },
    ],
  },
  {
    key: "document",
    title: "📝 Document",
    items: [
      { path: "/app/cti-narrative", label: "CTI Advisor", icon: ClipboardList },
      { path: "/app/f2f-encounter", label: "F2F Encounter", icon: UserCheck },
      { path: "/app/scribe", label: "AI Scribe", icon: Sparkles },
      { path: "/app/voice-note", label: "Voice Note + Coach", icon: Mic },
      { path: "/app/voice-diarization", label: "Voice Diarization", icon: Mic },
      { path: "/app/idg-meeting-prep", label: "IDG Meeting Prep", icon: Users },
      { path: "/app/bulk-notes", label: "Bulk Notes", icon: FileSpreadsheet },
      { path: "/app/coding-assistant", label: "AI Coding Assistant", icon: FileCode },
      { path: "/app/field-mode", label: "Field Mode (Mobile)", icon: Smartphone },
      { path: "/app/templates", label: "Templates", icon: BookOpen },
      { path: "/app/scribe-history", label: "Scribe History", icon: History },
      { path: "/app/cti-history", label: "CTI/F2F History", icon: History },
      { path: "/app/prognosis-signoff", label: "Prognosis Sign-Off", icon: FileSignature },
    ],
  },
  {
    key: "review",
    title: "🔍 Review",
    items: [
      { path: "/app/golden-thread", label: "Golden Thread", icon: Waypoints },
      { path: "/app/qa-review", label: "QA Review", icon: ClipboardCheck },
      { path: "/app/compliance-review", label: "Documentation Review", icon: FileSearch },
      { path: "/app/visit-audit", label: "CGS Audit Protection", icon: Shield },
      { path: "/app/upload", label: "Visit Audit", icon: Upload },
      { path: "/app/survey-readiness", label: "Survey Readiness", icon: ShieldCheck },
      { path: "/app/compliance-engine", label: "Compliance Check", icon: Shield },
      { path: "/app/verification", label: "Verification Sequence", icon: ShieldCheck },
      { path: "/app/compliance-coach", label: "Compliance Coach", icon: Sparkles },
      { path: "/app/compliance", label: "Compliance AI", icon: MessageSquare },
    ],
  },
  {
    key: "bill",
    title: "🧾 Bill",
    items: [
      { path: "/app/bill-ready", label: "Bill-Ready Gate", icon: Receipt },
      { path: "/app/compliance-autopilot", label: "Compliance Autopilot", icon: CalendarClock },
      { path: "/app/deadline-autocomplete", label: "Deadline Auto-Complete", icon: CalendarClock },
    ],
  },
  {
    key: "program",
    title: "🧭 Program Integrity",
    items: [
      { path: "/app/sfp-simulator", label: "SFP Risk Simulator", icon: ShieldAlert },
      { path: "/app/ssvi", label: "SSVI Analytics", icon: Activity },
      { path: "/app/ssvi/action-center", label: "SSVI Action Center", icon: ClipboardCheck },
      { path: "/app/ssvi/non-hospice", label: "Non-Hospice Spend", icon: DollarSign },
      { path: "/app/ssvi/benchmarking", label: "SSVI Benchmarking", icon: Activity },
      { path: "/app/ssvi/data", label: "SSVI Data Center", icon: Database },
      { path: "/app/cahps-optimizer", label: "CAHPS Optimizer", icon: BarChart3 },
      { path: "/app/tpe-tracker", label: "TPE Remediation", icon: ShieldCheck },
      { path: "/app/credential-tracker", label: "Credential Tracker", icon: BadgeCheck },
      { path: "/app/regulatory-feed", label: "Regulatory Updates", icon: Newspaper },
      { path: "/app/compliance-calendar", label: "Compliance Calendar", icon: Calendar },
    ],
  },
  {
    key: "defend",
    title: "🛡️ Defend",
    items: [
      { path: "/app/audit-center", label: "Audit Center", icon: ShieldCheck },
      { path: "/app/audit-defense", label: "Defense Generator", icon: FileCheck },
      { path: "/app/adr-response-builder", label: "ADR Response Builder", icon: FileSignature },
      { path: "/app/adr-workflow", label: "ADR Response", icon: Scale },
      { path: "/app/tpe-audit", label: "TPE Audit Simulator", icon: ShieldCheck },
      { path: "/app/advanced-compliance", label: "Advanced Tools", icon: Zap },
      { path: "/app/payer-intelligence", label: "Payer Intelligence", icon: Building2 },
      { path: "/app/cgs-audit-history", label: "Defense History", icon: History },
      { path: "/app/history", label: "Audit History", icon: History },
    ],
  },
  {
    key: "executive",
    title: "📊 Executive",
    items: [
      { path: "/app/executive", label: "Analytics Dashboard", icon: BarChart3 },
      { path: "/app/roi", label: "ROI / Money Saved", icon: DollarSign },
      { path: "/app/onepager-roi", label: "One-Pager ROI", icon: FileText },
      { path: "/app/defense-executive-review", label: "Defense Exec Review", icon: ShieldCheck },
      { path: "/app/risk-cockpit", label: "Predictive Risk Cockpit", icon: Target },
      { path: "/app/network", label: "Network Oversight", icon: Network },
      { path: "/app/network/modules", label: "Modules & Pricing", icon: DollarSign },
    ],
  },
  {
    key: "trust_admin",
    title: "🔒 Trust & Admin",
    items: [
      { path: "/app/hipaa-proof", label: "HIPAA Proof Center", icon: FileSignature },
      { path: "/app/hipaa-compliance", label: "HIPAA Center", icon: ShieldCheck },
      { path: "/app/phi-audit", label: "PHI Audit Trail", icon: Shield },
      { path: "/app/audit-logs", label: "Audit Logs", icon: Shield },
      { path: "/app/admin/security", label: "Security & HIPAA", icon: Shield },
      { path: "/app/admin/truth-safety", label: "Truth & Safety", icon: ShieldCheck },
      { path: "/app/admin/system-status", label: "System Status", icon: Activity },
      { path: "/app/admin/diagnostics", label: "Deploy Diagnostics", icon: Activity },
      { path: "/app/admin/pending-starters", label: "Pending Starters", icon: KeyRound },
      { path: "/app/admin/white-label", label: "White-Label Domains", icon: Globe },
      { path: "/app/emr-connection", label: "Connect Your EMR", icon: Plug },
      { path: "/app/team-data", label: "Team & Data", icon: UsersRound },
      { path: "/app/organization", label: "Organization", icon: Building },
      { path: "/app/users", label: "User Management", icon: Users },
      { path: "/app/branding", label: "Branding", icon: Palette },
      { path: "/app/domains", label: "Domains", icon: Globe },
      { path: "/app/pricing", label: "Billing & Plans", icon: CreditCard },
      { path: "/app/certification", label: "Training", icon: GraduationCap },
      { path: "/app/my-credentials", label: "My Credentials", icon: BadgeCheck },
      { path: "/app/settings", label: "Settings", icon: Settings },
      { path: "/app/super-admin", label: "Platform Admin", icon: Crown },
      { path: "/app/module-provisioning", label: "Module Provisioning", icon: Package },
      { path: "/app/partnership-inbox", label: "Partnership Inbox", icon: Inbox },
    ],
  },
];

const LEADERSHIP = ["admin", "super_admin", "compliance", "clinical_manager"];

/**
 * The exact set of paths a role is allowed to see in the sidebar. Mirrors the
 * legacy Layout.jsx branching 1:1 so no role gains or loses access — duplicate
 * links (Defense History, ROI) are collapsed and the legacy integration/launcher
 * pages are dropped from nav (still reachable by URL).
 */
export function allowedPathsFor(ctx) {
  const {
    role, isAdmin, isSuperAdmin, isPhysician, isNetworkAdmin, isStaff,
    canAccessDocumentationTools,
  } = ctx;
  const p = new Set();
  const add = (...paths) => paths.forEach((x) => p.add(x));

  // Universal
  add("/app/feature-hub", "/app/my-credentials");

  // Network admin — read-only rollup only.
  if (isNetworkAdmin) {
    add("/app/network", "/app/settings");
    return p;
  }
  add("/app/my-day");

  // Leadership gets the Readiness/Program/Bill cockpit (added before branching
  // in the legacy layout, so admins get it too).
  if (LEADERSHIP.includes(role)) {
    add(
      "/app/readiness", "/app/sfp-simulator", "/app/hope-cockpit",
      "/app/census-sync", "/app/deadline-autocomplete", "/app/daily-digest",
      "/app/compliance-autopilot", "/app/compliance-queue", "/app/bill-ready",
      "/app/credential-tracker", "/app/tpe-tracker", "/app/defense-executive-review",
      "/app/cahps-optimizer", "/app/ssvi/benchmarking", "/app/audit-center",
      "/app/golden-thread", "/app/patient-journey", "/app/care-teams",
    );
  }

  // Admin / super-admin organization pages.
  if (["admin", "super_admin"].includes(role)) {
    add("/app/team-data", "/app/emr-connection");
  }

  if (isStaff && !canAccessDocumentationTools) {
    // Basic staff — documentation capture only.
    add(
      "/app/scribe", "/app/voice-note", "/app/voice-diarization",
      "/app/idg-meeting-prep", "/app/certification", "/app/settings",
    );
  } else if (isStaff && canAccessDocumentationTools) {
    // Provider staff (clinical discipline).
    add(
      "/app/cti-narrative", "/app/f2f-encounter", "/app/scribe", "/app/voice-note",
      "/app/voice-diarization", "/app/scribe-history", "/app/cti-history", "/app/prognosis-signoff",
      "/app/idg-meeting-prep", "/app/templates", "/app/visit-audit", "/app/upload",
      "/app/eligibility", "/app/hope-validation", "/app/patient-tracker",
      "/app/certification", "/app/settings", "/app/patient-journey",
    );
  } else if (isPhysician && !isAdmin) {
    // Physician.
    add(
      "/app/cti-narrative", "/app/f2f-encounter", "/app/scribe", "/app/voice-diarization",
      "/app/scribe-history", "/app/cti-history", "/app/prognosis-signoff", "/app/idg-meeting-prep", "/app/templates",
      "/app/visit-audit", "/app/upload", "/app/eligibility", "/app/hope-validation",
      "/app/patient-tracker", "/app/bulk-notes", "/app/audit-defense",
      "/app/adr-response-builder", "/app/cgs-audit-history", "/app/advanced-compliance",
      "/app/qa-review", "/app/payer-intelligence", "/app/adr-workflow", "/app/tpe-audit",
      "/app/compliance-coach", "/app/compliance-review", "/app/executive",
      "/app/risk-cockpit", "/app/roi", "/app/coding-assistant", "/app/field-mode",
      "/app/regulatory-feed", "/app/golden-thread", "/app/settings", "/app/patient-journey",
    );
  } else if (!isPhysician && !isAdmin && !isStaff) {
    // Regular user (auditor / manager; includes compliance & clinical_manager).
    add(
      "/app/dashboard", "/app/visit-audit", "/app/upload", "/app/eligibility",
      "/app/hope-validation", "/app/survey-readiness", "/app/compliance-engine",
      "/app/audit-defense", "/app/history", "/app/executive", "/app/onepager-roi",
      "/app/compliance-calendar", "/app/settings",
    );
  } else {
    // Admin (full platform).
    add(
      // Document
      "/app/cti-narrative", "/app/f2f-encounter", "/app/scribe", "/app/voice-diarization",
      "/app/scribe-history", "/app/cti-history", "/app/prognosis-signoff", "/app/idg-meeting-prep", "/app/templates",
      // Patients
      "/app/patient-tracker", "/app/bulk-notes",
      // Review / QA
      "/app/qa-review", "/app/payer-intelligence", "/app/adr-workflow", "/app/compliance-coach",
      "/app/visit-audit", "/app/upload", "/app/eligibility", "/app/hope-validation",
      "/app/survey-readiness", "/app/compliance-engine", "/app/compliance-review",
      // Defend
      "/app/audit-defense", "/app/cgs-audit-history", "/app/advanced-compliance", "/app/tpe-audit",
      // Executive
      "/app/dashboard", "/app/executive", "/app/roi", "/app/compliance-calendar",
      "/app/regulatory-feed",
      // Trust & Admin
      "/app/hipaa-proof", "/app/hipaa-compliance", "/app/phi-audit", "/app/compliance-alerts",
      "/app/compliance", "/app/certification",
      // SSVI
      "/app/ssvi", "/app/ssvi/action-center", "/app/ssvi/non-hospice", "/app/ssvi/data",
      // Administration
      "/app/users", "/app/admin/pending-starters", "/app/admin/diagnostics", "/app/organization",
      "/app/audit-logs", "/app/admin/security", "/app/admin/system-status", "/app/admin/truth-safety",
      "/app/admin/white-label", "/app/pricing", "/app/branding", "/app/domains", "/app/settings",
    );
    if (isSuperAdmin) {
      add(
        "/app/network", "/app/network/modules", "/app/super-admin",
        "/app/module-provisioning", "/app/partnership-inbox",
      );
    }
  }

  return p;
}

/**
 * Clinician role — a lean sidebar of ONLY the à-la-carte tools the agency has
 * purchased. Kept here (not in Layout) so the entire nav model lives in one file.
 */
export const CLINICIAN_TOOLS = [
  { path: "/app/eligibility", label: "Eligibility Check", module: "eligibility" },
  { path: "/app/cti-narrative", label: "CTI Advisor", module: "cti_advisor" },
  { path: "/app/scribe", label: "AI Scribe", module: "ai_scribe" },
  { path: "/app/audit-defense", label: "Defense Generator", module: "defense_generator" },
  { path: "/app/qa-review", label: "Doc Review", module: "doc_review" },
];

export function buildClinicianSections(alacarte) {
  return [{
    title: "Clinician Tools",
    items: [
      { path: "/app/my-day", label: "My Day", icon: Sun },
      ...CLINICIAN_TOOLS
        .filter((t) => !alacarte?.alacarte || alacarte?.enabled?.[t.module] !== false)
        .map((t) => ({ path: t.path, label: t.label, icon: ClipboardList })),
    ],
  }];
}

// Clinicians' short menu: the tools they use every day, in workflow order. Leadership keeps the full menu.
const DAILY_TOOLS = {
  staff: ["/app/my-day", "/app/scribe", "/app/voice-note", "/app/idg-meeting-prep"],
  provider: ["/app/my-day", "/app/patient-journey", "/app/scribe", "/app/voice-note", "/app/scribe-history",
    "/app/idg-meeting-prep", "/app/patient-tracker"],
  physician: ["/app/my-day", "/app/prognosis-signoff", "/app/cti-narrative", "/app/f2f-encounter", "/app/cti-history",
    "/app/scribe-history", "/app/idg-meeting-prep", "/app/patient-journey"],
};

export function dailyToolsFor(ctx) {
  if (ctx.isNetworkAdmin || ctx.isAdmin) return null;
  if (ctx.isStaff) return ctx.canAccessDocumentationTools ? DAILY_TOOLS.provider : DAILY_TOOLS.staff;
  return ctx.isPhysician ? DAILY_TOOLS.physician : null;
}

export function buildDailySections(sections, paths) {
  const all = sections.flatMap((s) => s.items);
  const items = paths.map((p) => all.find((i) => i.path === p)).filter(Boolean);
  return [{ title: "📌 Your day", items }];
}

/**
 * Build the role-filtered, de-duplicated nav sections for rendering. Injects the
 * dynamic unread-compliance-alerts badge on Deploy Diagnostics (admins).
 */
export function buildNavSections(ctx) {
  const allowed = allowedPathsFor(ctx);
  const unread = ctx.unreadComplianceAlerts || 0;

  return NAV_SECTIONS
    .map((section) => ({
      title: section.title,
      items: section.items
        .filter((item) => allowed.has(item.path))
        .map((item) => {
          if (item.path === "/app/admin/diagnostics" && unread > 0) {
            return {
              ...item,
              badge: unread,
              badgeClass: "bg-amber-500 hover:bg-amber-500",
              badgeTestId: "sidebar-compliance-inbox-badge",
            };
          }
          return item;
        }),
    }))
    .filter((section) => section.items.length > 0);
}
